/**
 * DownloadedAssetsSection - Masonry grid of downloaded media assets.
 * Mirrors the picker popup layout (`media-picker-popup.tsx`) — natural
 * aspect ratios via `react-masonry-css`, no `object-cover` resampling,
 * cheap hover transitions only. Scrolling stays smooth even with
 * hundreds of items.
 */

import { useCallback, useMemo, useState } from "react";

import {
  FolderOpen,
  Loader2,
  Trash2,
  Check,
  Copy,
  Link2,
  Music,
  Play,
  Image as ImageIcon,
  Video as VideoIcon,
} from "lucide-react";

import Masonry from "react-masonry-css";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  MediaLibraryAsset,
  MediaType,
} from "@/types/media-library-panel.types";
import { formatFileSize } from "./media-utils";
import { useInfiniteScroll } from "./use-infinite-scroll";
import { cn } from "@/lib/utils";

const TYPE_TABS: Array<{
  id: MediaType;
  label: string;
  icon: typeof ImageIcon;
}> = [
  { id: "image", label: "Images", icon: ImageIcon },
  { id: "video", label: "Videos", icon: VideoIcon },
  { id: "audio", label: "Sounds", icon: Music },
];

interface DownloadedAssetsSectionProps {
  assets: MediaLibraryAsset[];
  totalAssets: number;
  /** Server-side per-type totals. Preferred over counting `assets` locally
   *  since pagination means `assets` only holds loaded pages. Falls back
   *  to bucket counts when not provided for backward compatibility. */
  countByType?: { image: number; video: number; audio: number };
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDeleteSelected: () => void;
  isLoading?: boolean;
  isDeleting?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export function DownloadedAssetsSection({
  assets,
  totalAssets,
  countByType,
  selectedIds,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  isLoading = false,
  isDeleting = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: DownloadedAssetsSectionProps): React.ReactElement {
  const [previewAsset, setPreviewAsset] = useState<MediaLibraryAsset | null>(
    null,
  );
  const [isCopied, setIsCopied] = useState(false);
  const [activeType, setActiveType] = useState<MediaType>("image");

  // Partition assets by type once; each tab reads its own bucket without a
  // per-render filter pass. Counts in the tab pills stay accurate even as
  // pagination appends new items.
  const typeBuckets = useMemo(() => {
    const buckets: Record<MediaType, MediaLibraryAsset[]> = {
      image: [],
      video: [],
      audio: [],
    };
    for (const a of assets) buckets[a.type]?.push(a);
    return buckets;
  }, [assets]);

  const visibleAssets = typeBuckets[activeType];

  const sentinelRef = useInfiniteScroll({
    onLoadMore: onLoadMore ?? (() => {}),
    hasMore,
    isLoading: isLoadingMore,
  });

  // Tab switch clears the current selection. The parent `selectedIds` set
  // is shared across all tabs, so without this, IDs from the previous tab
  // would linger invisibly (no selection indicator to reveal them) and
  // silently get deleted on the next "Delete" click.
  const handleTabChange = useCallback(
    (nextType: MediaType) => {
      if (nextType === activeType) return;
      if (selectedIds.size > 0) onClearSelection();
      setActiveType(nextType);
    },
    [activeType, selectedIds.size, onClearSelection],
  );

  // Scope "Select All" to the currently-visible tab. The parent's
  // `onSelectAll` callback selects every loaded asset across types, which
  // is surprising UX on a tabbed view. Toggling one-by-one via
  // `onToggleSelection` avoids a new prop on the interface.
  const handleSelectAllVisible = useCallback(() => {
    for (const asset of visibleAssets) {
      if (!selectedIds.has(asset.id)) onToggleSelection(asset.id);
    }
  }, [visibleAssets, selectedIds, onToggleSelection]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
        <span className="text-xs text-muted-foreground mt-2">
          Loading assets...
        </span>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
          <FolderOpen className="w-5 h-5 text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          No downloaded assets
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Search and download media to see them here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Type tabs — filter the masonry to a single media type. Counts come
          straight from the partitioned buckets so they stay in sync with
          pagination/download/delete without a second pass. */}
      <div className="flex items-stretch border-b border-border">
        {TYPE_TABS.map((tab) => {
          const Icon = tab.icon;
          // Prefer the server-side total (accurate even mid-pagination).
          // Fall back to the loaded bucket length only when the prop
          // isn't wired up yet.
          const count = countByType
            ? countByType[tab.id]
            : typeBuckets[tab.id].length;
          const isActive = activeType === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium",
                "transition-colors duration-150 border-b-2 -mb-px",
                isActive
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
              {count > 0 && (
                <span
                  className={cn(
                    "ml-0.5 text-[10px] tabular-nums",
                    isActive ? "text-primary" : "text-muted-foreground/60",
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Actions bar. Count reflects the active tab only — the top tab
          pills already expose per-type counts plus the overall total, so
          repeating "X total" here was confusing ("3 sounds · 99 total"
          read as "3 out of 99 sounds"). Uses the server-side total from
          `countByType` so it stays accurate before all pages have
          loaded. */}
      <div className="flex items-center justify-between">
        {(() => {
          const activeCount = countByType
            ? countByType[activeType]
            : visibleAssets.length;
          return (
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              {activeCount}{" "}
              {activeType === "image"
                ? `image${activeCount !== 1 ? "s" : ""}`
                : activeType === "video"
                  ? `video${activeCount !== 1 ? "s" : ""}`
                  : `sound${activeCount !== 1 ? "s" : ""}`}
            </span>
          );
        })()}
        <div className="flex items-center gap-1.5">
          {selectedIds.size > 0 ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={onClearSelection}
              >
                Clear
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-7 px-2.5 text-xs rounded-full gap-1"
                onClick={onDeleteSelected}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-3 h-3" />
                    Delete ({selectedIds.size})
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={handleSelectAllVisible}
              disabled={visibleAssets.length === 0}
            >
              Select All
            </Button>
          )}
        </div>
      </div>

      {/* Render all three type buckets simultaneously and toggle visibility
          via CSS. Switching between Images / Videos / Sounds used to re-key
          the whole masonry, unmounting every `<img>` / `<video>` and causing
          the browser to re-request each R2 asset on tab re-entry. Keeping
          all three subtrees mounted preserves the already-fetched resources
          (and their decoded pixel data) across tab switches — returning to
          a tab is instant. */}
      {TYPE_TABS.map((tab) => {
        const bucket = typeBuckets[tab.id];
        const isActive = activeType === tab.id;
        const EmptyIcon =
          tab.id === "image"
            ? ImageIcon
            : tab.id === "video"
              ? VideoIcon
              : Music;

        return (
          <div key={tab.id} className={cn(!isActive && "hidden")}>
            {bucket.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center mb-2">
                  <EmptyIcon className="w-4 h-4 text-muted-foreground/50" />
                </div>
                <p className="text-xs font-medium text-muted-foreground">
                  {tab.id === "image"
                    ? "No downloaded images"
                    : tab.id === "video"
                      ? "No downloaded videos"
                      : "No downloaded sounds"}
                </p>
              </div>
            ) : tab.id === "audio" ? (
              /* Audio — compact vertical list. Filenames are the primary
                 affordance (not visual thumbnails), and a 3-col masonry
                 with long filenames forced card widths past the panel
                 edge. Each row is a full-width card so filenames get
                 room to truncate cleanly. */
              <div className="flex flex-col gap-1.5">
                {bucket.map((asset) => {
                  const isSelected = selectedIds.has(asset.id);
                  return (
                    <div
                      key={asset.id}
                      onClick={() => setPreviewAsset(asset)}
                      className={cn(
                        "group relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-blue-500/10",
                        "transition-colors duration-150 border",
                        isSelected
                          ? "border-primary ring-2 ring-primary"
                          : "border-border hover:border-primary",
                      )}
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex-shrink-0 flex items-center justify-center">
                        <Music className="w-3.5 h-3.5 text-purple-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-xs font-medium text-foreground truncate"
                          title={asset.name}
                        >
                          {asset.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground tabular-nums">
                          {formatFileSize(asset.size)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleSelection(asset.id);
                        }}
                        className={cn(
                          "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0",
                          "transition-opacity duration-150",
                          isSelected
                            ? "bg-primary"
                            : "bg-muted-foreground/20 opacity-0 group-hover:opacity-100",
                        )}
                      >
                        {isSelected ? (
                          <Check className="w-3 h-3 text-primary-foreground" />
                        ) : (
                          <div className="w-2 h-2 rounded-full border-[1.5px] border-muted-foreground/50" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Masonry
                breakpointCols={3}
                className="flex -ml-2 w-full"
                columnClassName="pl-2 bg-clip-padding min-w-0"
              >
                {bucket.map((asset) => {
                  const isSelected = selectedIds.has(asset.id);
                  return (
                    <div
                      key={asset.id}
                      onClick={() => setPreviewAsset(asset)}
                      style={{
                        contentVisibility: "auto",
                        containIntrinsicSize: "auto 160px",
                      }}
                      className={cn(
                        "group relative mb-2 rounded-lg overflow-hidden cursor-pointer bg-muted",
                        "transition-colors duration-150",
                        "border",
                        isSelected
                          ? "border-primary ring-2 ring-primary"
                          : "border-border hover:border-primary",
                      )}
                    >
                      {/* Selection indicator */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleSelection(asset.id);
                        }}
                        className={cn(
                          "absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center z-10",
                          "transition-opacity duration-150",
                          isSelected
                            ? "bg-primary"
                            : "bg-black/30 backdrop-blur-sm opacity-0 group-hover:opacity-100",
                        )}
                      >
                        {isSelected ? (
                          <Check className="w-3 h-3 text-primary-foreground" />
                        ) : (
                          <div className="w-2 h-2 rounded-full border-[1.5px] border-white" />
                        )}
                      </button>

                      {/* Thumbnail — natural aspect via `h-auto`; no
                          `object-cover` so the browser doesn't resample
                          pixels per card. `decoding="async"` moves image
                          decode off the main thread. */}
                      {asset.type === "image" && asset.previewUrl ? (
                        <img
                          src={asset.previewUrl}
                          alt={asset.name}
                          className="w-full h-auto block"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : asset.type === "video" && asset.previewUrl ? (
                        <div className="relative">
                          <video
                            src={asset.previewUrl}
                            className="w-full h-auto block"
                            muted
                            preload="metadata"
                          />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-9 h-9 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center">
                              <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                            </div>
                          </div>
                        </div>
                      ) : asset.type === "audio" ? (
                        <div className="w-full aspect-[4/3] bg-gradient-to-br from-purple-500/15 via-pink-500/10 to-blue-500/15 flex flex-col items-center justify-center gap-1.5 px-2">
                          <div className="w-9 h-9 rounded-full bg-purple-500/20 flex items-center justify-center">
                            <Music className="w-4 h-4 text-purple-500" />
                          </div>
                          <span
                            className="text-[10px] text-foreground/70 font-medium truncate max-w-full"
                            title={asset.name}
                          >
                            {asset.name}
                          </span>
                        </div>
                      ) : (
                        <div className="w-full aspect-[4/3] flex items-center justify-center">
                          <FolderOpen className="w-6 h-6 text-muted-foreground/30" />
                        </div>
                      )}

                      {/* Info overlay — hover-only. */}
                      <div className="absolute inset-x-0 bottom-0 pt-6 pb-1.5 px-2 bg-gradient-to-t from-black/60 via-black/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <div className="flex items-center justify-between gap-1 text-[10px] text-white/90 font-medium">
                          <span className="capitalize truncate">
                            {asset.type}
                          </span>
                          <span className="tabular-nums shrink-0">
                            {formatFileSize(asset.size)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Skeleton cards while loading more — only on the
                    active tab so we don't duplicate spinners across
                    hidden tabs. */}
                {isActive &&
                  isLoadingMore &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="rounded-lg overflow-hidden animate-pulse mb-2"
                    >
                      <div className="w-full aspect-[4/3] bg-muted" />
                    </div>
                  ))}
              </Masonry>
            )}
          </div>
        );
      })}

      {/* Infinite scroll sentinel */}
      {hasMore && <div ref={sentinelRef} className="h-1" />}

      {/* Preview dialog */}
      <Dialog
        open={previewAsset !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewAsset(null);
        }}
      >
        {previewAsset && (
          <DialogContent className="max-w-[92vw] sm:max-w-3xl max-h-[85dvh] p-0 overflow-hidden bg-black/95 border-border gap-0 rounded-lg">
            <DialogTitle className="sr-only">{previewAsset.name}</DialogTitle>
            <div className="relative flex items-center justify-center">
              {previewAsset.type === "image" && previewAsset.previewUrl ? (
                <img
                  src={previewAsset.previewUrl}
                  alt={previewAsset.name}
                  className="w-full object-contain max-h-[60dvh]"
                />
              ) : previewAsset.type === "video" && previewAsset.previewUrl ? (
                <video
                  src={previewAsset.previewUrl}
                  controls
                  autoPlay
                  className="w-full object-contain max-h-[60dvh]"
                  playsInline
                />
              ) : previewAsset.type === "audio" && previewAsset.previewUrl ? (
                <div className="w-full flex flex-col items-center justify-center py-16 gap-4">
                  <div className="w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
                    <Music className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                  <audio
                    src={previewAsset.previewUrl}
                    controls
                    autoPlay
                    className="w-4/5 max-w-md"
                  />
                </div>
              ) : (
                <div className="w-full flex items-center justify-center py-20">
                  <FolderOpen className="w-12 h-12 text-muted-foreground/30" />
                </div>
              )}
            </div>
            <div className="px-4 py-3 bg-background space-y-2 overflow-hidden">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {previewAsset.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="capitalize">{previewAsset.type}</span>
                    {" · "}
                    {formatFileSize(previewAsset.size)}
                  </p>
                </div>
              </div>
              {previewAsset.previewUrl && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border/50 overflow-hidden">
                  <Link2 className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
                  <span
                    className="flex-1 min-w-0 text-xs text-muted-foreground/70 truncate font-mono"
                    title={previewAsset.previewUrl}
                  >
                    {previewAsset.assetPath}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0 hover:bg-muted-foreground/10"
                    onClick={() => {
                      navigator.clipboard.writeText(previewAsset.assetPath);
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 1200);
                    }}
                  >
                    {isCopied ? (
                      <Check className="w-3 h-3 text-primary" />
                    ) : (
                      <Copy className="w-3 h-3 text-muted-foreground/50" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
